/**
 * Spec 76 Phase B (host) + Phase E (a11y / focus return).
 *
 * Reads URL state via `useContextPanel`, looks up the registered panel type,
 * and renders the right panel with a header bar and close button.
 *
 * Renders nothing when no panel is open. Shows an inline error card when
 * the URL references an unregistered panel type (graceful, no crash).
 *
 * Two layouts: `overlay` slides in over the page's right edge (the detailed conversation page); `docked` is a column of
 * the page's own layout (the simple view's records column, owner 2026-10-31), whose close is a way back to the list.
 * A record's panel (a type with a `label`) shows the record's own detail screen inside `EntityPanelScope`, with a link
 * to that screen in the detailed view; a person without the screen's policy is told so instead.
 *
 * Phase E hardening:
 *   - Esc closes the panel.
 *   - On open, the close button auto-focuses (so keyboard users land in the
 *     panel without tab-traversing the page first).
 *   - On close, focus is restored to whatever element opened the panel —
 *     captured via `document.activeElement` at open time.
 *   - The panel is not modal (chat below remains usable), so we use
 *     `role="complementary"` rather than `role="dialog"` and DO NOT trap
 *     focus or set `aria-modal`.
 */
import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ExternalLink, X } from 'lucide-react';
import { Can } from '../auth/Can.js';
import { EntityPanelScopeContext } from '../page/entity-panel-scope.js';
import { Button } from '../ui/Button.js';
import { sentenceCase } from '../ui/FilterSelect.js';
import { useOpenInDetailedView } from './open-in-detailed-view.js';
import { useContextPanel } from './url-state.js';
import { getPanelType } from './registry.js';

interface ContextPanelProps {
  layout?: 'overlay' | 'docked';
}

export const ContextPanel: React.FC<ContextPanelProps> = ({ layout = 'overlay' }) => {
  const { panel, closePanel } = useContextPanel();
  const openInDetailedView = useOpenInDetailedView();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Close on Escape key while panel is open.
  useEffect(() => {
    if (panel === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panel, closePanel]);

  // Focus management: capture previous focus on open, focus close button,
  // restore on close. Keyed on `panel === null` so each open/close cycle
  // captures fresh state.
  const isOpen = panel !== null;
  useEffect(() => {
    if (!isOpen) return;

    // Capture whatever was focused before the panel opened so we can return
    // to it on close. Falls back to `document.body` if nothing was focused.
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Focus the close button so Esc/Enter immediately work for keyboard users.
    panelRef.current?.querySelector<HTMLElement>('[data-close-panel]')?.focus();

    return () => {
      // Restore focus on unmount (panel close). Guard against the previous
      // element being detached from the DOM (rare but possible if the opener
      // was removed during the panel session).
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) {
        prev.focus();
      }
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  if (panel === null) return null;

  const definition = getPanelType(panel.type);
  const label = definition?.label ?? sentenceCase(panel.type);
  const detailHref = definition?.detailHref?.(panel.id) ?? null;
  const docked = layout === 'docked';
  const body =
    definition === null ? (
      <div className="m-4 px-4 py-3 rounded-lg border bg-surface-alt text-sm text-danger">
        This panel is not available.
      </div>
    ) : (
      definition.render({ id: panel.id, onClose: closePanel })
    );

  return (
    <div
      className={
        docked
          ? 'flex h-full min-h-0 min-w-0 flex-col bg-surface'
          : [
              'fixed inset-y-0 right-0 z-40',
              'w-full lg:w-[540px]',
              'flex flex-col',
              'bg-surface border-l',
              'shadow-xl',
              'transition-transform duration-200',
              isOpen ? 'translate-x-0' : 'translate-x-full',
            ].join(' ')
      }
      ref={panelRef}
      role="complementary"
      aria-label={`${label} panel`}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0">
        <div className="flex min-w-0 items-center gap-1">
          {docked && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={closePanel}
              aria-label="Back to the list"
              data-close-panel
            >
              <ArrowLeft size={16} aria-hidden="true" />
            </Button>
          )}
          <span className="text-sm font-medium text-heading truncate">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {detailHref && (
            <Button variant="ghost" size="sm" onClick={() => openInDetailedView(detailHref)}>
              <ExternalLink size={14} aria-hidden="true" />
              Open in detailed view
            </Button>
          )}
          {!docked && (
            <Button variant="ghost" size="sm" iconOnly onClick={closePanel} aria-label="Close panel" data-close-panel>
              <X size={16} aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {definition?.label ? (
          // A record's own screen, shown as part of this page (`entity-panel-scope.ts`).
          <EntityPanelScopeContext value={true}>
            <div className="p-4">
              {definition.policy ? (
                <Can
                  policy={definition.policy}
                  fallback={<p className="text-sm text-muted">You do not have access to this {label.toLowerCase()}.</p>}
                >
                  {body}
                </Can>
              ) : (
                body
              )}
            </div>
          </EntityPanelScopeContext>
        ) : (
          body
        )}
      </div>
    </div>
  );
};
